// "취소로 정리" 확인 모달 — superuser 전용.
//
// 배송까지 나간 주문에서 취소가 감지되면 자동 전환하지 않고 경고만 뜬다. 회수·환불을
// 확인한 담당자가 여기서 마무리한다.
//
// ⚠️ 사유는 필수다(서버도 막는다). 배송된 건을 취소로 닫는 판단이라 근거가 없으면
//    나중에 설명할 수 없다.
// ⚠️ 카페24를 호출하지 않는다 — 카페24 쪽은 이미 취소·환불된 상태이고 ERP가 따라가는 것이다.
// 환불 금액·일자(2026-10-16): 함께 저장하면 매출 현황의 "주문 취소 환불"이 된다. 기본값은 서버
//    refund_suggestions(카페24 결제금액 감소분·환불일). 저장이 실패하면 취소 정리도 함께 되돌린다.
// ⚠️ 배경 클릭 닫기는 useBackdropClose 훅만 쓴다(CLAUDE.md) — 입력 중 드래그로 값이
//    날아가는 것을 막는다.
import React, { useEffect, useState } from "react";

import api from "../../api";
import useBackdropClose from "../../hooks/useBackdropClose";
import "./rental.css";

export default function CancelResolveModal({ order, busy, onClose, onConfirm }) {
  const backdrop = useBackdropClose(onClose);
  const [reason, setReason] = useState("");
  const [localError, setLocalError] = useState("");
  const [withRefund, setWithRefund] = useState(true);
  const [refund, setRefund] = useState({ amount: "", refunded_on: "", method: "CARD_PARTIAL" });

  // 목록 카드에는 환불 기본값이 없어 상세를 한 번 읽는다(order.refund_suggestions).
  useEffect(() => {
    let alive = true;
    const fill = (detail) => {
      const s = (detail.refund_suggestions || []).find((x) => x.kind === "CANCEL");
      if (alive && s) setRefund({ amount: s.amount, refunded_on: s.refunded_on, method: s.method });
    };
    if (order.refund_suggestions) fill(order);
    else api.get(`rental/orders/${order.id}/`).then((res) => fill(res.data)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [order]);

  const submit = () => {
    if (!reason.trim()) {
      setLocalError("정리 사유를 입력해 주세요.");
      return;
    }
    if (withRefund && (!refund.amount || !refund.refunded_on)) {
      setLocalError("환불 금액과 환불일을 입력하거나, '환불 기록도 저장'을 해제해 주세요.");
      return;
    }
    setLocalError("");
    onConfirm({ reason: reason.trim(), ...(withRefund ? { refund } : {}) });
  };

  return (
    <div className="rental-overlay" {...backdrop}>
      <div className="rental-modal">
        <h3 className="rental-section-title">
          취소로 정리 — {order.receiver_name || order.order_id}
        </h3>

        <div className="rental-warn-box">
          <strong>⚠ 취소 요청 — 확인 필요</strong>
          {order.cancel_detail ? (
            <div className="rental-warn-reason">{order.cancel_detail}</div>
          ) : null}
        </div>

        <div className="rental-row">
          <span className="rental-row-label">현재 상태</span>
          <span className="rental-row-value">{order.status_label}</span>
        </div>

        <div className="rental-row">
          <span className="rental-row-label">정리 사유</span>
        </div>
        <textarea
          className="rental-memo"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="회수·환불 확인 내용을 적습니다 (필수)"
        />

        <label className="rental-check" style={{ marginTop: 10 }}>
          <input type="checkbox" checked={withRefund} onChange={(e) => setWithRefund(e.target.checked)} />
          환불 기록도 저장 (매출 현황 "주문 취소 환불")
        </label>
        {withRefund && (
          <>
            <div className="rental-row">
              <span className="rental-row-label">환불 금액</span>
              <input type="number" min="0" className="rental-input rental-refund-field" value={refund.amount}
                     onChange={(e) => setRefund({ ...refund, amount: e.target.value })} />
            </div>
            <div className="rental-row">
              <span className="rental-row-label">환불일</span>
              <input type="date" className="rental-input rental-refund-field" value={refund.refunded_on}
                     onChange={(e) => setRefund({ ...refund, refunded_on: e.target.value })} />
            </div>
            <div className="rental-row">
              <span className="rental-row-label">환불 수단</span>
              <select className="rental-input rental-refund-field" value={refund.method}
                      onChange={(e) => setRefund({ ...refund, method: e.target.value })}>
                <option value="CARD_PARTIAL">카드 부분취소</option>
                <option value="TRANSFER">계좌이체</option>
                <option value="OTHER">기타</option>
              </select>
            </div>
          </>
        )}

        <div className="rental-auto-hint" style={{ display: "block" }}>
          카페24로는 아무것도 보내지 않습니다. ERP 상태만 취소로 정리하고 이력에 남깁니다.
        </div>

        {localError && <div className="rental-error">{localError}</div>}

        <div className="rental-modal-actions">
          <button type="button" className="rental-btn-cancel" onClick={onClose}>
            닫기
          </button>
          <button
            type="button"
            className="rental-btn-confirm"
            onClick={submit}
            disabled={busy}
          >
            {busy ? "처리 중…" : "취소로 정리"}
          </button>
        </div>
      </div>
    </div>
  );
}
